import { useEffect, useState } from "react";
import type { ReactElement } from "react";

interface Top {
  handle: string;
  value: number;
}
type GameId =
  | "reflex"
  | "wheel"
  | "aim"
  | "crash"
  | "stop"
  | "mines"
  | "limbo"
  | "plinko"
  | "draw"
  | "rocket";

type Tops = Record<GameId, Top | null> & {
  /** Ids switched off via the kill switch — rendered greyed and unplayable. */
  retired?: string[];
};

interface GameDef {
  id: GameId;
  name: string;
  hook: string;
  url: string;
  /** Render the leader's brag number with its unit. */
  fmt: (v: number) => string;
  topLabel: string;
}

const GAMES: GameDef[] = [
  {
    id: "reflex",
    name: "reflex",
    hook: "how fast can you react? 5 taps, one number.",
    url: "https://reflex.karrtopelka.com",
    fmt: (v) => `${v}ms`,
    topLabel: "fastest",
  },
  {
    id: "wheel",
    name: "wheel",
    hook: "push your luck. grow your streak — or bust.",
    url: "https://wheel.karrtopelka.com",
    fmt: (v) => `${v} streak`,
    topLabel: "biggest",
  },
  {
    id: "aim",
    name: "aim",
    hook: "tap every target. 20 seconds. how high can you go?",
    url: "https://aim.karrtopelka.com",
    fmt: (v) => `${v} hits`,
    topLabel: "best",
  },
  {
    id: "crash",
    name: "crash",
    hook: "the multiplier climbs. cash out before it crashes.",
    url: "https://crash.karrtopelka.com",
    fmt: (v) => `${(v / 100).toFixed(2)}x`,
    topLabel: "highest",
  },
  {
    id: "stop",
    name: "stop",
    hook: "stop the clock at exactly 5.00s. blind after 2.",
    url: "https://stop.karrtopelka.com",
    fmt: (v) => `${v}ms off`,
    topLabel: "closest",
  },
  {
    id: "mines",
    name: "mines",
    hook: "tap gems, dodge 4 mines, cash out before the boom.",
    url: "https://mines.karrtopelka.com",
    fmt: (v) => `${(v / 100).toFixed(2)}x`,
    topLabel: "biggest sweep",
  },
  {
    id: "limbo",
    name: "limbo",
    hook: "call a multiplier, then watch the dice decide.",
    url: "https://limbo.karrtopelka.com",
    fmt: (v) => `${(v / 100).toFixed(2)}x`,
    topLabel: "boldest call",
  },
  {
    id: "plinko",
    name: "plinko",
    hook: "one ball, 12 rows of pegs, 175x on the edges.",
    url: "https://plinko.karrtopelka.com",
    fmt: (v) => `${(v / 100).toFixed(2)}x`,
    topLabel: "luckiest bucket",
  },
  {
    id: "draw",
    name: "draw",
    hook: "pick a number. 19:00 UTC. everyone watches the same reveal.",
    url: "https://draw.karrtopelka.com",
    fmt: (v) => `${v} 🔮`,
    topLabel: "longest streak",
  },
  {
    id: "rocket",
    name: "rocket",
    hook: "one shared rocket every 10 min — everyone rides, who bails best?",
    url: "https://rocket.karrtopelka.com",
    fmt: (v) => `${(v / 100).toFixed(2)}x`,
    topLabel: "best escape",
  },
];

/* ------------------------------------------------------------------ */
/* per-game inline motif icons — stroke inherits currentColor (accent) */
/* ------------------------------------------------------------------ */

const MOTIFS: Record<GameId, ReactElement> = {
  reflex: (
    // lightning bolt
    <path d="M13 2 6 13h5l-1 9 7-11h-5l1-9z" />
  ),
  wheel: (
    // spoked wheel
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M12 3v6.4M12 14.6V21M3 12h6.4M14.6 12H21" />
    </>
  ),
  aim: (
    // crosshair
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  crash: (
    // rising curve with arrow tip
    <>
      <path d="M3 20c6 0 12-4 17-15" />
      <path d="M16.8 4.6 20 5l.4 3.3" />
    </>
  ),
  stop: (
    // stopwatch
    <>
      <circle cx="12" cy="14" r="7.5" />
      <path d="M12 14V9.8M9.5 2h5M12 2v4.5" />
    </>
  ),
  mines: (
    // faceted gem
    <>
      <path d="M12 3l7 6-7 12L5 9l7-6z" />
      <path d="M5 9h14M12 3 9 9l3 12M12 3l3 6-3 12" />
    </>
  ),
  limbo: (
    // die, five pips
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="8.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="8.5" cy="15.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="15.5" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  plinko: (
    // peg triangle
    <>
      <circle cx="12" cy="5.5" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="8.8" cy="11" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15.2" cy="11" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="16.5" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="12" cy="16.5" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="16.5" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  draw: (
    // crystal orb on a base
    <>
      <circle cx="12" cy="10.5" r="7" />
      <path d="M8.7 16.7 7 21h10l-1.7-4.3" />
      <path d="M8.8 8.3a4.2 4.2 0 0 1 3-2" />
    </>
  ),
  rocket: (
    // rocket with fins + flame
    <>
      <path d="M12 2c2.8 2.2 4.2 5.4 4.2 8.8 0 2-.4 3.7-1.2 5.2H9c-.8-1.5-1.2-3.2-1.2-5.2C7.8 7.4 9.2 4.2 12 2z" />
      <circle cx="12" cy="9.2" r="1.7" />
      <path d="M9 13.2 6.6 17h2.6M15 13.2l2.4 3.8h-2.6" />
      <path d="M10.8 18.6 12 22l1.2-3.4" />
    </>
  ),
};

function Motif({ id }: { id: GameId }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-8 w-8 shrink-0 text-accent transition-all duration-300 group-hover:animate-float group-hover:drop-shadow-[0_0_8px_rgba(255,111,165,0.55)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {MOTIFS[id]}
    </svg>
  );
}

/* ------------------------------------------------------------ */
/* live event countdowns (draw @ 19:00 UTC, rocket every 10 min) */
/* ------------------------------------------------------------ */

const pad2 = (n: number): string => String(n).padStart(2, "0");

function msToNextDraw(now: number): number {
  const d = new Date(now);
  let target = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 19, 0, 0);
  if (target <= now) target += 86_400_000;
  return target - now;
}

function msToNextLaunch(now: number): number {
  const period = 600_000; // one shared rocket every 10 minutes
  const rem = period - (now % period);
  return rem === 0 ? period : rem;
}

function fmtHMS(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
}

function fmtMS(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;
}

function EventChip({
  label,
  getRemaining,
  format,
}: {
  label: string;
  getRemaining: (now: number) => number;
  format: (ms: number) => string;
}) {
  const [remaining, setRemaining] = useState(() => getRemaining(Date.now()));
  useEffect(() => {
    const id = setInterval(() => setRemaining(getRemaining(Date.now())), 1000);
    return () => clearInterval(id);
  }, [getRemaining]);
  return (
    <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-0.5 font-mono text-[11px] text-accent">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent animate-breathe" />
      {label} {format(remaining)}
    </span>
  );
}

/* --------------- */
/* the hub itself  */
/* --------------- */

export function App() {
  const [tops, setTops] = useState<Tops | null>(null);

  useEffect(() => {
    fetch("/api/tops")
      .then((r) => r.json() as Promise<Tops>)
      .then(setTops)
      .catch(() =>
        setTops({
          reflex: null,
          wheel: null,
          aim: null,
          crash: null,
          stop: null,
          mines: null,
          limbo: null,
          plinko: null,
          draw: null,
          rocket: null,
        }),
      );
  }, []);

  const retired = new Set(tops?.retired ?? []);
  const liveCount = GAMES.filter((g) => !retired.has(g.id)).length;

  // ticker entries: one per game with a live top score; hide the strip if none
  const tickerEntries = tops
    ? GAMES.flatMap((g) => {
        const t = tops[g.id];
        if (!t) return [];
        const handle = t.handle.startsWith("@") ? t.handle : `@${t.handle}`;
        return [{ id: g.id, name: g.name, value: g.fmt(t.value), handle }];
      })
    : [];

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center bg-bg text-text"
      style={{
        paddingTop: "calc(3rem + env(safe-area-inset-top))",
        paddingBottom: "calc(3rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center px-6 text-center">
        <h1 className="font-display text-5xl font-extrabold tracking-tight lg:text-7xl">
          karrtopelka
          <span
            className="text-accent"
            style={{ textShadow: "0 0 24px rgba(255,111,165,0.65)" }}
          >
            .
          </span>
        </h1>
        <p className="mt-3 text-text-muted lg:text-lg">
          tiny games. one number to beat. pick one.
        </p>
        <p className="mt-4 inline-flex animate-neon-pulse items-center rounded-full border border-accent/25 bg-surface px-3.5 py-1 font-mono text-xs text-text-muted">
          <span className="text-accent">{liveCount}</span>&nbsp;games · beat the timeline
        </p>
      </header>

      {tickerEntries.length > 0 && (
        <div
          className="mt-8 w-full animate-fade-up overflow-hidden border-y border-border py-2"
          style={{ animationDelay: "80ms" }}
        >
          <div className="flex w-max animate-ticker whitespace-nowrap font-mono text-xs text-text-muted hover:[animation-play-state:paused]">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex" aria-hidden={dup === 1}>
                {tickerEntries.map((e) => (
                  <span key={`${dup}-${e.id}`} className="px-5">
                    {e.name} <span className="text-accent">{e.value}</span> — {e.handle}
                    <span className="pl-10 text-text-faint">·</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex w-full justify-center px-6">
        <div className="grid w-full max-w-md gap-4 sm:max-w-6xl sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
          {GAMES.map((g, i) => {
            const top = tops?.[g.id] ?? null;
            const off = retired.has(g.id);

            // A retired card keeps its shape but loses colour, hover lift and link —
            // the sprawl still reads as a system, this one is just switched off.
            const body = (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Motif id={g.id} />
                    <span className="text-2xl font-bold">
                      {g.name}
                      <span className={off ? "text-text-faint" : "text-accent"}>.</span>
                    </span>
                  </div>
                  {off ? (
                    <span className="rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-text-faint">
                      retired
                    </span>
                  ) : (
                    <span className="text-sm text-text-faint transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-accent">
                      play →
                    </span>
                  )}
                </div>
                <p className="text-sm text-text-muted">{g.hook}</p>
                {!off && g.id === "draw" && (
                  <EventChip label="next draw" getRemaining={msToNextDraw} format={fmtHMS} />
                )}
                {!off && g.id === "rocket" && (
                  <EventChip label="next launch" getRemaining={msToNextLaunch} format={fmtMS} />
                )}
                <div className="mt-auto border-t border-border pt-3 text-sm">
                  {tops === null ? (
                    <span className="inline-block h-3.5 w-36 animate-breathe rounded bg-surface-raised" />
                  ) : off ? (
                    <span className="text-text-faint">off the board — scores archived</span>
                  ) : top ? (
                    <span className="text-text-muted">
                      <span className="font-display text-xs font-bold text-gold">#1</span>{" "}
                      {g.topLabel}:{" "}
                      <span className="font-display font-semibold text-text lg:text-base">
                        {g.fmt(top.value)}
                      </span>{" "}
                      by {top.handle}
                    </span>
                  ) : (
                    <span className="text-text-faint">no scores yet — go first</span>
                  )}
                </div>
              </>
            );

            return (
              <div
                key={g.id}
                className="animate-fade-up"
                style={{ animationDelay: `${160 + i * 60}ms` }}
              >
                {off ? (
                  <div
                    className="flex h-full flex-col gap-3 rounded-lg border border-border bg-surface p-5 opacity-45 grayscale"
                    aria-disabled="true"
                  >
                    {body}
                  </div>
                ) : (
                  <a
                    href={g.url}
                    className="group flex h-full flex-col gap-3 rounded-lg border border-border bg-surface p-5 transition-all duration-200 hover:-translate-y-1 hover:border-accent hover:shadow-glow"
                  >
                    {body}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <footer
        className="mt-12 animate-fade-up px-6 text-xs text-text-faint"
        style={{ animationDelay: "780ms" }}
      >
        more games soon · built for the timeline ·{" "}
        <a href="/subdomains" className="transition-colors hover:text-accent">
          all subdomains
        </a>
      </footer>
    </main>
  );
}
